'use client';

import type { ClassValue } from 'clsx';

import { clsxm } from '@vs-shop/utils';

import {
  CollapsibleBlock,
  CollapsibleBlockCollapseLabel,
  CollapsibleBlockContent,
  CollapsibleBlockExpandLabel,
  CollapsibleBlockToggle,
} from '@vs-shop/ui/collapsibleBlock';
import type { ClampLines } from '@vs-shop/ui/clampedText';
import { useIntl } from 'react-intl';

import Markdown from '../server/typography/Markdown';

type Props = {
  content: string;
  lines: ClampLines;
  className?: ClassValue;
};

/**
 * rcprofi's clamped description: this tenant's markdown inside the shared
 * clamp-and-reveal block.
 *
 * The block owns the measurement and the control; what stays here is the
 * renderer, the copy and the decision that empty content renders nothing at all
 * rather than an empty box with a control under it.
 *
 * The control keeps the `data-slot` the route assertions already follow — a
 * call site naming a slot is naming the thing it composed.
 */
export default function CollapsibleMarkdown({ content, lines, className }: Props) {
  const intl = useIntl();

  if (!content || content.trim() === '') {
    return null;
  }

  return (
    <CollapsibleBlock lines={lines} className={clsxm(className)}>
      <CollapsibleBlockContent render={<Markdown>{content}</Markdown>} />

      <CollapsibleBlockToggle data-slot="collapsible-markdown-toggle">
        <CollapsibleBlockExpandLabel>
          {intl.formatMessage({
            id: 'collapsibleMarkdown.readMore',
            defaultMessage: 'Přečíst celé',
          })}
        </CollapsibleBlockExpandLabel>
        <CollapsibleBlockCollapseLabel>
          {intl.formatMessage({
            id: 'collapsibleMarkdown.collapse',
            defaultMessage: 'Skrýt',
          })}
        </CollapsibleBlockCollapseLabel>
      </CollapsibleBlockToggle>
    </CollapsibleBlock>
  );
}
